import { useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { getUTMParams } from '../utils/utm';
import { getReferral } from '../utils/referral';
import { SITUATIONS, INTERESTS, GOALS, CHALLENGES, TIMELINES } from '../constants/networkSnapshot';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const input = 'w-full h-12 px-4 border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-shadow';

const EMPTY = {
  full_name: '', email: '', phone: '', college: '', degree: '', current_situation: '', whatsapp_consent: false,
  career_interest: '', career_interest_other: '', career_goal: '', career_challenge: '', career_challenge_other: '',
  opportunity_timeline: '', city: '', help_text: '',
};

const indianMobile = (raw) => {
  let d = raw.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  if (!/^[6-9]\d{9}$/.test(d) || new Set(d).size <= 2) return null;
  if ('01234567890123456789'.includes(d) || '98765432109876543210'.includes(d)) return null;
  if (d.slice(0, 5) === d.slice(5) || /(\d)\1{6}/.test(d)) return null;
  return d;
};

const validateStep1 = (f) => {
  if (f.full_name.trim().length < 2) return 'Please enter your full name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return 'Please enter a valid email address.';
  if (!indianMobile(f.phone)) return 'Please enter a valid 10-digit Indian mobile number.';
  if (!f.current_situation) return 'Please select your current situation.';
  return '';
};
const validateStep2 = (f) => {
  if (!f.career_interest) return 'Please choose the career field that interests you most.';
  if (f.career_interest === 'other' && !f.career_interest_other.trim()) return 'Please tell us which career field interests you.';
  if (!f.career_goal) return 'Please choose your main career goal.';
  if (!f.career_challenge) return 'Please choose your biggest career challenge.';
  if (f.career_challenge === 'other' && !f.career_challenge_other.trim()) return 'Please tell us your biggest challenge.';
  if (!f.opportunity_timeline) return 'Please tell us when you are looking for an internship or job.';
  return '';
};

const Choice = ({ name, options, value, onChange, cols = 'grid-cols-2' }) => (
  <div role="radiogroup" className={`grid ${cols || 'grid-cols-1'} gap-2`}>
    {options.map((o) => (
      <button type="button" key={o.value} role="radio" aria-checked={value === o.value} data-testid={`network-${name}-${o.value}`}
        onClick={() => onChange(name, o.value)}
        className={`text-left text-sm px-3 sm:px-4 py-3 rounded-xl border transition-colors ${value === o.value ? 'border-teal-600 bg-teal-50 text-[#0b1f4d] font-medium' : 'border-slate-200 text-slate-700 hover:border-teal-300'}`}>
        {o.label}
      </button>
    ))}
  </div>
);

const Question = ({ n, title, optional, children }) => (
  <fieldset className="space-y-2">
    <legend className="text-sm font-semibold text-slate-900 mb-2">{n}. {title} {optional ? <span className="font-normal text-slate-400">(optional)</span> : <span className="text-red-500">*</span>}</legend>
    {children}
  </fieldset>
);

const Progress = ({ step }) => (
  <div data-testid="network-progress" className="mb-6">
    <div className="flex justify-between text-xs font-medium text-slate-500 mb-2">
      <span className={step === 1 ? 'text-teal-700' : ''}>Step 1 · About You</span>
      <span className={step === 2 ? 'text-teal-700' : ''}>Step 2 · Your Career Goals</span>
    </div>
    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full bg-[#0b1f4d] rounded-full transition-[width] duration-500" style={{ width: step === 1 ? '50%' : '100%' }} />
    </div>
  </div>
);

export const NetworkSnapshotForm = ({ onJoined }) => {
  const [step, setStep] = useState(1);
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);

  const set = (name, value) => { setF((p) => ({ ...p, [name]: value })); setError(''); };
  const onInput = (e) => set(e.target.name, e.target.type === 'checkbox' ? e.target.checked : e.target.value);

  const next = async (e) => {
    e.preventDefault();
    const msg = validateStep1(f);
    if (msg) { setError(msg); return; }
    setChecking(true);
    try {
      const res = await fetch(`${API_URL}/api/network-pool/check-contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: f.email, phone: f.phone }),
      });
      const data = await res.json();
      if (res.ok && !data.ok) { setError(data.message); return; }
    } catch { /* final submit re-checks on the server */ } finally { setChecking(false); }
    if (window.gtag) window.gtag('event', 'talent_network_started');
    setStep(2);
    document.getElementById('join-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const msg = validateStep2(f);
    if (msg) { setError(msg); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/network-pool`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, current_status: f.current_situation, utmParams: getUTMParams(), referral_code: getReferral() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check your answers and try again.');
      if (window.gtag) window.gtag('event', 'talent_network_joined');
      onJoined({ ...f, id: data.id, readiness_token: data.readiness_token });
    } catch (err) { setError(err.message || 'Something went wrong. Please try again.'); } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 sm:p-8">
      <Progress step={step} />
      {step === 1 ? (
        <form onSubmit={next} noValidate className="space-y-4" data-testid="network-step1">
          <input name="full_name" value={f.full_name} onChange={onInput} placeholder="Full Name *" className={input} data-testid="network-name" />
          <input type="email" name="email" value={f.email} onChange={onInput} placeholder="Email Address *" className={input} data-testid="network-email" />
          <input type="tel" inputMode="numeric" maxLength={15} name="phone" value={f.phone} onChange={onInput} placeholder="WhatsApp Number (10-digit Indian mobile) *" className={input} data-testid="network-phone" />
          <input name="college" maxLength={200} value={f.college} onChange={onInput} placeholder="College / University (optional)" className={input} data-testid="network-college" />
          <input name="degree" maxLength={200} value={f.degree} onChange={onInput} placeholder="Course, e.g. B.Tech CSE, BBA (optional)" className={input} data-testid="network-course" />
          <select name="current_situation" value={f.current_situation} onChange={onInput} className={input} data-testid="network-status">
            <option value="">Current Situation *</option>
            {SITUATIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <label className="flex items-start gap-3 cursor-pointer pt-1">
            <input type="checkbox" name="whatsapp_consent" checked={f.whatsapp_consent} onChange={onInput} className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-700" data-testid="network-consent-checkbox" />
            <span className="text-sm text-slate-600">I agree to receive updates and relevant career-related follow-ups from Elevate Careers on WhatsApp. <span className="text-slate-400">(optional)</span></span>
          </label>
          {error && <p data-testid="network-error" className="text-sm text-red-600">{error}</p>}
          <button type="submit" disabled={checking} data-testid="network-next-btn" className="w-full py-4 rounded-full bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {checking ? 'Checking…' : 'Continue'} <ArrowRight className="w-5 h-5" />
          </button>
        </form>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-6" data-testid="network-step2">
          <Question n={1} title="Which career field interests you the most?">
            <Choice name="career_interest" options={INTERESTS} value={f.career_interest} onChange={set} />
            {f.career_interest === 'other' && <input name="career_interest_other" value={f.career_interest_other} onChange={onInput} maxLength={100} placeholder="Which field?" className={input} data-testid="network-interest-other" />}
          </Question>
          <Question n={2} title="What is your main career goal right now?">
            <Choice name="career_goal" options={GOALS} value={f.career_goal} onChange={set} />
          </Question>
          <Question n={3} title="What's your biggest career challenge right now?">
            <Choice name="career_challenge" options={CHALLENGES} value={f.career_challenge} onChange={set} cols="" />
            {f.career_challenge === 'other' && <input name="career_challenge_other" value={f.career_challenge_other} onChange={onInput} maxLength={200} placeholder="Tell us your challenge" className={input} data-testid="network-challenge-other" />}
          </Question>
          <Question n={4} title="When are you looking to pursue an internship or job?">
            <Choice name="opportunity_timeline" options={TIMELINES} value={f.opportunity_timeline} onChange={set} />
          </Question>
          <Question n={5} title="Which city are you currently based in?" optional>
            <input name="city" value={f.city} onChange={onInput} maxLength={80} placeholder="e.g. Pune" className={input} data-testid="network-city" />
          </Question>
          <Question n={6} title="Is there anything specific you'd like help with?" optional>
            <textarea name="help_text" value={f.help_text} onChange={onInput} maxLength={1000} rows={3} placeholder="Tell us briefly about your goal or what you're struggling with."
              className="w-full px-4 py-3 border border-slate-200 rounded-xl resize-none focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" data-testid="network-help" />
          </Question>
          {error && <p data-testid="network-error" className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={() => { setStep(1); setError(''); }} data-testid="network-back-btn"
              className="px-4 sm:px-5 py-4 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button type="submit" disabled={submitting} data-testid="network-submit"
              className="flex-1 px-4 py-4 rounded-full bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] text-sm sm:text-base font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Joining...</> : 'Join the Talent Network — Free'}
            </button>
          </div>
        </form>
      )}
      <p className="text-center text-xs text-slate-400 mt-5">
        Free to join. No employment or interview guarantee. Read our <a href="/privacy" target="_blank" rel="noreferrer" className="underline" data-testid="network-privacy-link">Privacy Policy</a>.
      </p>
    </div>
  );
};
